import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType, useRoutes } from "react-router-dom";
import AOS from "aos";

import { APP_ROUTES } from "../routes/AppRoutes";
import { routes } from "../routes/router";
import Footer from "../components/common/Footer";
import TopToBottom from "../components/common/TopToBottom";

const MainLayout = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Page transition: keep showing the old page (displayLocation) while the
  // veil fades in over it, then swap in the new one and fade the veil out.
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("page-enter");
  const page = useRoutes(routes, displayLocation);
  // Scroll position of every page visited, by history entry.
  const scrollPositions = useRef({});

  useEffect(() => {
    AOS.init({ duration: 700, easing: "ease-out-cubic" });
    window.history.scrollRestoration = "manual"; // we restore it ourselves
  }, []);

  // New page: back/forward returns to where that page was left; a normal
  // link starts at the top. Runs after the page's own effects (some call
  // scrollTo(0, 0) on mount), so the restored position wins.
  // "instant": the page sets scroll-behavior: smooth.
  useEffect(() => {
    const saved = navigationType === "POP" ? scrollPositions.current[displayLocation.key] : undefined;
    window.scrollTo({ top: saved ?? 0, left: 0, behavior: "instant" });
  }, [displayLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Links like "/#our-services" jump to that section once its page is showing.
  // "instant" so the jump doesn't glide through (and trip) the homepage intro.
  useEffect(() => {
    if (location.hash && navigationType !== "POP" && location.pathname === displayLocation.pathname) {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: "instant" });
    }
  }, [location, displayLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      setTransitionStage("page-exit");
    }
  }, [location, displayLocation]);

  const handleAnimationEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (transitionStage === "page-exit") {
      scrollPositions.current[displayLocation.key] = window.scrollY;
      setDisplayLocation(location);
      setTransitionStage("page-enter");
    }
  };

  const hideFooter = location.pathname.startsWith(
    APP_ROUTES.MOVIE_LINKS.GET_MOVIE_LINKS,
  );

  return (
    <>
      <div
        className={`page-transition-progress ${transitionStage === "page-exit" ? "is-active" : ""}`}
        aria-hidden="true"
      />
      <div
        className={`page-veil ${transitionStage}`}
        onAnimationEnd={handleAnimationEnd}
        aria-hidden="true"
      />
      <TopToBottom />
      <div className="page-transition">{page}</div>
      {!hideFooter && <Footer />}
    </>
  );
};

export default MainLayout;
